import { initializeApp } from "firebase/app";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import * as Sentry from "@sentry/react";
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from "firebase/functions";
import * as amplitude from "@amplitude/analytics-browser";
import {
  BACKEND_SERVER_URL,
  FASTAPI_SERVER_URL,
  LAMBDA_AWS_URL,
} from "./constants/aws-constants";

if (import.meta.env.MODE !== "development") {
  const replay = Sentry.replayIntegration({
    maskAllText: false,
    blockAllMedia: false,
    maskAllInputs: false,
    networkDetailAllowUrls: [
      LAMBDA_AWS_URL,
      `${BACKEND_SERVER_URL}/createSuite`,
      `${BACKEND_SERVER_URL}/curl`,
      FASTAPI_SERVER_URL,
      "identitytoolkit.googleapis.com",
      "https://us-central1-flutter-feedback.cloudfunctions.net",
      "http://device-hub.mobileboost.io",
      "https://device-hub.mobileboost.io",
      "https://android.mobileboost.io",
      "https://ios1.mobileboost.io",
      "https://ios2.mobileboost.io",
      "https://ios3.mobileboost.io",
      "https://ios4.mobileboost.io",
      "https://ios5.mobileboost.io",
      "https://ios6.mobileboost.io",
      "https://par-t1.mobileboost.io",
      "https://par-1.mobileboost.io",
      "https://par-1a.mobileboost.io",
      "https://par-1b.mobileboost.io",
      "https://par-1c.mobileboost.io",
      "https://par-1d.mobileboost.io",
      "https://par-1e.mobileboost.io",
      "https://par-1f.mobileboost.io",
      "https://par-1g.mobileboost.io",
      "https://par-1h.mobileboost.io",
      "https://par-1i.mobileboost.io",
      "https://par-1j.mobileboost.io",
      "https://par-1k.mobileboost.io",
      "https://par-1l.mobileboost.io",
      "https://par-1m.mobileboost.io",
      "https://par-1n.mobileboost.io",
      "https://par-1o.mobileboost.io",
      "https://par-1p.mobileboost.io",
      "https://par-1q.mobileboost.io",
      "https://par-1r.mobileboost.io",
      "https://par-1s.mobileboost.io",
      "https://par-1u.mobileboost.io",
      "https://bln-1.mobileboost.io",
      "https://bln-2.mobileboost.io",
    ],
  });

  Sentry.init({
    dsn: "https://a352a6302eca4c04afaf90cd4a0d5793@o4504564674002944.ingest.sentry.io/4505328943366144",
    enableLogs: true,
    sendDefaultPii: true,
    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 0,
    tracesSampleRate: 1.0,
    integrations: [
      replay,
      Sentry.browserTracingIntegration(),
      Sentry.consoleLoggingIntegration({
        levels: ["log", "warn", "error", "debug"],
      }),
    ],
  });

  amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
    defaultTracking: true,
  });
}

// When VITE_USE_EMULATORS=true the SDKs are wired to the local Firebase
// Emulator Suite instead of production (used by the agent devbox container).
const useEmulators = import.meta.env.VITE_USE_EMULATORS === "true";
const emulatorHost = import.meta.env.VITE_EMULATOR_HOST || "localhost";

const firebaseConfig = {
  apiKey: "AIzaSyDtzPICCCUZRjzk3VEoCEuR_RFDVoaFO7Q",
  authDomain: "flutter-feedback.firebaseapp.com",
  projectId: useEmulators
    ? import.meta.env.VITE_FIREBASE_PROJECT_ID || "flutter-feedback"
    : "flutter-feedback",
  storageBucket: "flutter-feedback.appspot.com",
  messagingSenderId: "186075198856",
  appId: "1:186075198856:web:f0f71c7f0ac8a58a7ab63d",
};

const app = initializeApp(firebaseConfig);

const customStorage = getStorage(app);
const customFirestore = getFirestore(app);
const firebaseAuth = getAuth(app);

const functions = getFunctions(app);

if (useEmulators) {
  connectAuthEmulator(firebaseAuth, `http://${emulatorHost}:9099`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(customFirestore, emulatorHost, 8080);
  connectStorageEmulator(customStorage, emulatorHost, 9199);
  connectFunctionsEmulator(functions, emulatorHost, 5001);
  // eslint-disable-next-line no-console
  console.info(
    `[firebase] connected to emulators at ${emulatorHost} (project ${firebaseConfig.projectId})`
  );
}
const customClaimsMigrationSetter = httpsCallable(
  functions,
  "customClaimsMigration"
);

const changeCompanyCustomClaimsSetter = httpsCallable(
  functions,
  "changeCompanyCustomClaims"
);

const tipTapCollabJWTGenerator = httpsCallable(
  functions,
  "tipTapCollabJWTGenerator"
);

const transferTemplatesHandler = httpsCallable(
  functions,
  "transferTemplatesHandler"
);

const cloneTestsHandler = httpsCallable(functions, "cloneTestsHandler");
const getAppDistributionReleasesHandler = httpsCallable(
  functions,
  "getAppDistributionReleasesHandler"
);
const exchangeOAuthCodeForToken = httpsCallable(
  functions,
  "exchangeOAuthCodeForTokenHandler"
);

const checkOAuthStatus = httpsCallable<
  null,
  { authorized: boolean; clientId: string | null }
>(functions, "checkOAuthStatusHandler");
const cloneMultipleReleasesHandler = httpsCallable(
  functions,
  "cloneMultipleReleasesHandler"
);

const cloneMultipleReleasesOAuthHandler = httpsCallable(
  functions,
  "cloneMultipleReleasesOAuthHandler"
);

const getAppDistributionReleasesOAuthHandler = httpsCallable(
  functions,
  "getAppDistributionReleasesOAuthHandler"
);

const getPylonVerificationHash = httpsCallable(
  functions,
  "pylonVerificationHashGenerator"
);

const checkSsoMethod = httpsCallable<
  { email: string },
  { method: "saml" | "password" }
>(functions, "checkSsoMethodHandler");

// Best-effort audit reporter for FAILED logins (successes are captured
// server-side by the beforeUserSignedIn blocking function). Unauthenticated by
// design — a failed login has no session.
const recordAuthEvent = httpsCallable<
  {
    event_type: "login.failed";
    email?: string;
    error_code?: string;
    method?: string;
  },
  { recorded: boolean }
>(functions, "recordAuthEvent");

export {
  recordAuthEvent,
  customStorage,
  customFirestore,
  firebaseAuth,
  customClaimsMigrationSetter,
  changeCompanyCustomClaimsSetter,
  tipTapCollabJWTGenerator,
  transferTemplatesHandler,
  cloneTestsHandler,
  functions,
  getAppDistributionReleasesHandler,
  cloneMultipleReleasesHandler,
  exchangeOAuthCodeForToken,
  checkOAuthStatus,
  getAppDistributionReleasesOAuthHandler,
  cloneMultipleReleasesOAuthHandler,
  getPylonVerificationHash,
  checkSsoMethod,
  app as firebaseApp,
};
